import { useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { HumanCheck, useHumanCheck } from "@/components/HumanCheck";

export default function SubscribeCard({ facility }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const human = useHumanCheck();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const payload = { email, facility_id: facility.id, ...human.fields };
      if (phone.trim()) payload.phone = phone.trim();
      const { data } = await apiClient.post("/subscriptions", payload);
      toast.success(data.message || "Subscribed");
      setSubscribed(true);
      setEmail("");
      setPhone("");
    } catch (err) {
      const detail = human.errorText(err.response?.data?.detail);
      toast.error(typeof detail === "string" ? detail : "Could not subscribe");
      human.refresh();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="subscribe-card">
      <div className="flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-blue-600 text-white"><BellRing size={14} /></div>
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900">{t("subscribe.title")}</h3>
          <p className="text-xs text-slate-500">{t("subscribe.subtitle")}</p>
        </div>
      </div>
      {subscribed ? (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="subscribe-success">
          {t("subscribe.success")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-2">
          <Input
            data-testid="subscribe-email-input"
            type="email"
            required
            placeholder={t("subscribe.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            data-testid="subscribe-phone-input"
            type="tel"
            placeholder={t("subscribe.phonePlaceholder")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <HumanCheck check={human} idPrefix="subscribe" />
          <Button data-testid="subscribe-submit-btn" type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {submitting ? "..." : t("subscribe.cta")}
          </Button>
        </form>
      )}
      <p className="mt-2 text-[11px] text-slate-500">{t("subscribe.privacy")}</p>
      {!subscribed && <p className="mt-1 text-[11px] text-slate-400">{t("subscribe.smsOff")}</p>}
    </Card>
  );
}
